/**
 * Formulation 模块数据转换函数
 */
import type {
  AgentMessageResponse,
  FormulationMessage,
  FormulationMessageFile,
  FORMULATION_MESSAGE_ROLE,
  FORMULATION_MESSAGE_STATUS,
  BusinessType,
  TaskStatus,
} from '@/types/formulation/formulation';
import type { SearchJsonDocItem, SearchDocOrgInfo } from './types';
import { TASK_STATUS, MESSAGE_STATUS } from './types';

/**
 * 将 API 返回的 business_type 转换为 UI 组件期望的 role
 */
export const mapBusinessTypeToRole = (businessType: BusinessType): FORMULATION_MESSAGE_ROLE => {
  const roleMap: Record<BusinessType, FORMULATION_MESSAGE_ROLE> = {
    user_input: 'user',
    user_message: 'ai',
    tool_display: 'tool',
  };
  return roleMap[businessType] ?? 'ai';
};

/**
 * 将 API 返回的 tool_state 转换为 UI 组件期望的 status
 */
export const mapToolStateToStatus = (toolState: TaskStatus): FORMULATION_MESSAGE_STATUS => {
  const statusMap: Record<TaskStatus, FORMULATION_MESSAGE_STATUS> = {
    [TASK_STATUS.RUNNING]: MESSAGE_STATUS.EXECUTING,
    [TASK_STATUS.STARTED]: MESSAGE_STATUS.EXECUTING,
    [TASK_STATUS.COMPLETED]: MESSAGE_STATUS.COMPLETED,
    [TASK_STATUS.FAILED]: MESSAGE_STATUS.ERROR,
    [TASK_STATUS.CANCELLED]: MESSAGE_STATUS.ERROR,
  };
  return statusMap[toolState] ?? MESSAGE_STATUS.PENDING;
};

/**
 * 将 AgentMessageResponse 转换为 FormulationMessage
 */
export const transformAgentMessageToFormulationMessage = (
  agent: AgentMessageResponse,
): FormulationMessage => {
  // 从 content 中提取消息文本
  let message = '';
  let files: FormulationMessageFile[] | undefined;

  if (agent.content) {
    if (typeof agent.content.raw === 'string') {
      message = agent.content.raw;
    } else if (typeof agent.content === 'object') {
      message = JSON.stringify(agent.content);
    }

    // 提取 files 数组（用户上传的文件信息）
    const rawFiles = (agent.content as { files?: unknown[] }).files;
    if (Array.isArray(rawFiles) && rawFiles.length > 0) {
      files = rawFiles
        .filter((f): f is Record<string, unknown> => typeof f === 'object' && f !== null)
        .map((f) => ({
          file_name: typeof f.file_name === 'string' ? f.file_name : '',
          file_path: typeof f.file_path === 'string' ? f.file_path : undefined,
          context_id: typeof f.context_id === 'number' ? f.context_id : undefined,
        }))
        .filter((f) => f.file_name);
    }
  }

  const timestamp = agent.gmt_created ? new Date(agent.gmt_created).getTime() : 0;

  // 当 business_type 为 exception 时，强制设置 status 为 error
  const status =
    agent.business_type === 'exception'
      ? MESSAGE_STATUS.ERROR
      : mapToolStateToStatus(agent.tool_state);

  return {
    id: agent.id,
    clientKey: agent.clientKey,
    role: mapBusinessTypeToRole(agent.business_type),
    message,
    iteration: agent.iteration,
    status,
    timestamp: Number.isNaN(timestamp) ? 0 : timestamp,
    tool_key: agent.tool_key,
    meta: agent.meta,
    files,
  };
};

/** 类型安全的属性提取辅助函数 */
const getString = (val: unknown): string | undefined => (typeof val === 'string' ? val : undefined);

const getNumber = (val: unknown): number | undefined => (typeof val === 'number' ? val : undefined);

/**
 * 解析 ORG_INFO 数组
 */
function parseOrgInfo(orgInfo: unknown): SearchDocOrgInfo[] | undefined {
  if (!Array.isArray(orgInfo)) return undefined;
  return orgInfo
    .filter((org): org is Record<string, unknown> => typeof org === 'object' && org !== null)
    .map((org) => ({
      entity_id: getString(org.entity_id),
      id: getString(org.id) || getString(org.entity_id),
      name: getString(org.name),
      name_cn: getString(org.name_cn),
      name_en: getString(org.name_en),
      display_name: getString(org.display_name),
      normalized_name: getString(org.normalized_name),
      logo: getString(org.logo),
      country_name: getString(org.country_name),
      website: getString(org.website),
      entity_type: getString(org.entity_type),
    }));
}

/**
 * 将 content.raw（数组）转为 SearchJsonDocItem[]，用于 citation 与列表展示
 */
export function mapRawToSearchDocs(raw: unknown): SearchJsonDocItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item: Record<string, unknown>) => ({
    pbd: getString(item?.pbd),
    apd: getString(item?.apd),
    TITLE: getString(item?.TITLE),
    PN: getString(item?.PN),
    doc_id: getString(item?.doc_id) ?? String(item?.doc_id ?? ''),
    CONTENT: getString(item?.CONTENT) ?? '',
    doc_type: item?.doc_type === 'patent' ? 'patent' : 'paper',
    rrf_score: getNumber(item?.rrf_score),
    country: getString(item?.country),
    ans: Array.isArray(item?.ans) ? item.ans : undefined,
    ORG_INFO: parseOrgInfo(item?.ORG_INFO),
  }));
}
